// OIDC token store for the SPA.
//
// Deliberately separate from the session (src/auth/session.tsx): the session
// answers "is someone signed in?" (and is also seeded directly by the E2E harness
// and the external-learner email-link flow, which carry no Keycloak token), while
// this holds the Keycloak access/refresh/id tokens that `lib/api.ts` attaches as a
// Bearer header and the SCORM transport carries. Persisted to localStorage so a
// page reload keeps the tokens; an in-memory cache avoids re-parsing on every call.

export interface StoredTokens {
  accessToken: string;
  refreshToken?: string;
  /** id_token — used as the id_token_hint for RP-initiated logout and for claims. */
  idToken?: string;
  /** Epoch ms when the access token expires (from `expires_in` at issue time). */
  expiresAt: number;
}

const TOKENS_KEY = "clms.tokens";

function read(): StoredTokens | null {
  try {
    const raw = localStorage.getItem(TOKENS_KEY);
    return raw ? (JSON.parse(raw) as StoredTokens) : null;
  } catch {
    return null;
  }
}

let cache: StoredTokens | null = read();

export function getTokens(): StoredTokens | null {
  return cache;
}

/** Current access token, if any (no refresh — see oidc.getValidAccessToken). */
export function getAccessToken(): string | undefined {
  return cache?.accessToken;
}

export function getIdToken(): string | undefined {
  return cache?.idToken;
}

export function setTokens(tokens: StoredTokens | null): void {
  cache = tokens;
  try {
    if (tokens) localStorage.setItem(TOKENS_KEY, JSON.stringify(tokens));
    else localStorage.removeItem(TOKENS_KEY);
  } catch {
    // Storage unavailable/denied (private mode, quota): the in-memory cache still
    // serves this tab; auth just won't survive a reload.
  }
}

export function clearTokens(): void {
  setTokens(null);
}

/**
 * Same stored pair? Compared on the two CREDENTIALS rather than on object identity: the pair parsed back
 * out of localStorage on a reload is a different object holding the same tokens, and it is the same pair.
 * The other two fields are derived from the same grant (`idToken` is minted with it, `expiresAt` from its
 * `expires_in`), so the credentials settle the question on their own.
 */
function samePair(a: StoredTokens | null, b: StoredTokens | null): boolean {
  if (a === b) return true;
  if (!a || !b) return false;
  return a.accessToken === b.accessToken && a.refreshToken === b.refreshToken;
}

export interface TokenWrite {
  /** Whether `next` was stored — false when the pair had already been replaced. */
  written: boolean;
  /** What the store holds NOW: `next` on a win, the pair that overtook it on a loss. */
  tokens: StoredTokens | null;
}

/**
 * Replace the stored pair only while it is still the one the caller read — and on a loss, answer with the
 * pair now held rather than with the write that did not happen.
 *
 * WHY A CONDITIONAL WRITE EXISTS HERE AT ALL. `setTokens`/`clearTokens` land on whatever the store holds
 * when they are called, which is the right answer for a write whose caller IS the authority: the code
 * exchange in `oidc.handleCallback` has just established the session and its pair supersedes everything.
 * It is the wrong answer for a write decided earlier and applied later. `oidc.refresh` is exactly that: it
 * is handed a pair by `getValidAccessToken`, spends a network round-trip, and then writes — and the window
 * it writes into is the one ADR-0021 §6.2 is about, where the callback document boots on the PREVIOUS
 * login's tokens while `AuthCallbackPage`'s own exchange is in flight (see `codeExchangePending`). A
 * rejected grant clearing the store there empties a session the product established milliseconds earlier,
 * and the next bearer-less request's plain 401 bounces the person back to the login page; an accepted one
 * overwrites the new pair with one minted from the old grant, which where the new pair is the one finally
 * carrying `amr: ["otp"]` is a silent downgrade back to a token §6 refuses (task 7e7fd3fb, filed from
 * 4ae414c1).
 *
 * This module is the home for it because it owns the pair and the cache that serves it, so the comparison
 * and the write cannot be interleaved by anything else — JS has no preemption, and both halves are
 * synchronous here. A caller that compared `getTokens()` itself and then called `setTokens` would be
 * writing the same race one level up.
 */
export function compareAndSetTokens(
  expected: StoredTokens | null,
  next: StoredTokens | null,
): TokenWrite {
  if (!samePair(cache, expected)) return { written: false, tokens: cache };
  setTokens(next);
  return { written: true, tokens: next };
}
